import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { useParams } from "react-router-dom";

import { PageImport, PageImportStats } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import Button from "~/components/mantine/button/Button";
import { SiteWithAuth } from "~/components/siteSettings/General.tsx";
import Spinner from "~/components/ui/spinner/Spinner";
import { canMember } from "~/helpers/authorizations";
import { CRAWL_MODE } from "~/helpers/consts";
import { PageImportForm } from "~/pages/dashboard/PageImportForm";
import { PageImportResponseWithAuth } from "~/types/dashboard.ts";

import styles from "./Whitelist.module.scss";

const LABELS: Record<string, string> = {
  created: "Created",
  updated: "Updated",
  duplicate: "Duplicate",
  invalid: "Invalid",
};

const FINALIZED_STATUSES = ["error", "completed"];

type WhitelistProps = {
  siteData: SiteWithAuth | undefined;
  isLoading: boolean;
};

function StatsBlocks({
  stats,
  isFinalized,
}: {
  stats: PageImportStats;
  isFinalized: boolean;
}) {
  return (
    <div className={styles.statsContainer}>
      {Object.entries(stats).map(([key, value]) => (
        <div key={key} className={styles.statsBlock}>
          <div
            className={clsx(styles.progressBar, {
              [styles.progressBarAnimated]: !isFinalized,
            })}
          ></div>
          <div className={styles.statsDataWrapper}>
            <div className={styles.statsLabel}>{LABELS[key] ?? key}</div>
            <div className={styles.statsValue}>
              {Number(value).toLocaleString()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const StatusText = ({ pageImport }: { pageImport: PageImport }) => {
  if (pageImport.status === "completed") {
    if (pageImport.has_pages) {
      return (
        <>
          <div>
            All Automations from the pages that are not included into the list
            (if any) will be removed.
          </div>
          <p>
            {`We have parsed the uploaded file successfully and identified ${pageImport.total_count}`}{" "}
            pages in total. You will be able to review the skipped URLs from the
            Site’s settings page.
          </p>
        </>
      );
    } else {
      return (
        <div>
          We have not found any valid URLs in your file. Please upload another
          file.
        </div>
      );
    }
  } else if (pageImport.status === "error") {
    return <div>Unable to read and import your file.</div>;
  } else {
    return <div>Import in progress</div>;
  }
};

const Whitelist = ({ siteData, isLoading: isLoadingSite }: WhitelistProps) => {
  const queryClient = useQueryClient();
  const { id: siteId } = useParams();
  const site = siteData?.site;
  const crawlMode = site?.crawl_mode;

  // @ts-expect-error TODO: remove after site.page_import API schema would be fixed and would exclude String type
  const pageImportId = site?.page_import?.id;
  const isWhitelisted = crawlMode === CRAWL_MODE.WHITELISTED;

  const { mutate: retryImport, isPending: isRetryImportPending } = useMutation({
    mutationKey: ["pageImport", pageImportId, "retry"],
    mutationFn: async ({
      siteId,
      pageImportId,
    }: {
      siteId: number;
      pageImportId: number;
    }) => {
      return apiClient.sites.pageImportsRetryCreate(siteId, pageImportId);
    },
    onSuccess: async (_data, vars) => {
      await queryClient.invalidateQueries({
        queryKey: ["pageImport", vars.pageImportId],
      });
    },
  });

  const { data: pageImportData } = useQuery({
    queryKey: ["pageImport", pageImportId],
    enabled: isWhitelisted && !!siteId && !!pageImportId,
    queryFn: async (): Promise<PageImportResponseWithAuth> => {
      const response = await apiClient.sites.pageImportsDetail(
        Number(siteId),
        Number(pageImportId),
        {
          headers: {
            "Authorize-Member-Actions": "retry",
          },
        },
      );

      return response.data;
    },
  });

  const pageImport = pageImportData?.page_import;

  if (isLoadingSite || !site) {
    return <Spinner />;
  } else if (!isWhitelisted || !pageImportId || !pageImport) {
    return <p>This site does not crawl pages from the list</p>;
  }

  const retryPermission = canMember(pageImportData.authorizations, "retry");
  const pageImportStats = pageImport.stats;

  const isFinalized = FINALIZED_STATUSES.includes(pageImport.status);
  const inputFile = pageImport.input_file;
  const canRetry =
    retryPermission &&
    Number.isFinite(Number(siteId)) &&
    Number.isFinite(Number(pageImportId));

  return (
    <div className={styles.page}>
      <article className={styles.container}>
        <h2>Last Import</h2>
        {<StatusText pageImport={pageImport} />}
        {pageImportStats && (
          <StatsBlocks stats={pageImportStats} isFinalized={isFinalized} />
        )}
        {canRetry && (
          <Button
            onClick={() =>
              retryImport({
                siteId: Number(siteId),
                pageImportId: Number(pageImportId),
              })
            }
            disabled={isRetryImportPending}
          >
            {isRetryImportPending && <Spinner size="1rem" />}
            Retry
          </Button>
        )}

        {isFinalized && (
          <div className={styles.actionsBlock}>
            <h2>Update Whitelist</h2>
            <PageImportForm existingFile={inputFile} />
          </div>
        )}
      </article>
    </div>
  );
};

export default Whitelist;
